import {
  ActionIcon,
  Button,
  Card,
  Group,
  Menu,
  Modal,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconBook,
  IconChevronDown,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@/lib/icons";
import { Pencil } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Mkb10FormDialog } from "@/features/references/Mkb10FormDialog";
import {
  type Mkb10Read,
  deleteMkb10,
  searchMkb10,
} from "@/lib/api/references";
import { useAuthStore } from "@/stores/auth";

export function Mkb10Page() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasPermission("reference.update"));
  const [q, setQ] = useState("");
  const [debQ] = useDebouncedValue(q, 300);
  const [dialogOpen, dialogHandlers] = useDisclosure(false);
  const [editing, setEditing] = useState<Mkb10Read | null>(null);
  const [deleting, setDeleting] = useState<Mkb10Read | null>(null);
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(1);

  // Reset to first page whenever query or page-size changes
  const onSearchChange = (v: string) => {
    setQ(v);
    setPage(1);
  };
  const onPageSizeChange = (v: string | null) => {
    if (v) {
      setPageSize(Number(v));
      setPage(1);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ["mkb10-page", debQ, pageSize, page],
    queryFn: () => searchMkb10(debQ, pageSize, (page - 1) * pageSize),
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const deleteMutation = useMutation({
    mutationFn: (code: string) => deleteMkb10(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mkb10-page"] });
      queryClient.invalidateQueries({ queryKey: ["mkb10"] });
      notifications.show({ color: "green", message: t("references.mkb_deleted") });
    },
  });

  const lang = i18n.language;
  const getDescription = (item: Mkb10Read) => {
    if (lang === "uz") return item.description_uz ?? item.description_ru;
    if (lang === "en") return item.description_en ?? item.description_ru;
    return item.description_ru;
  };

  const onAdd = () => {
    setEditing(null);
    dialogHandlers.open();
  };

  const onEdit = (row: Mkb10Read) => {
    setEditing(row);
    dialogHandlers.open();
  };

  const onDelete = (row: Mkb10Read) => setDeleting(row);

  const confirmDelete = () => {
    if (deleting) {
      deleteMutation.mutate(deleting.code, {
        onSuccess: () => setDeleting(null),
      });
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("references.mkb10")}
        subtitle={data ? `${total}` : undefined}
        back
        actions={
          canEdit ? (
            <Button
              leftSection={<IconPlus size={16} strokeWidth={2.4} />}
              onClick={onAdd}
              radius="xl"
            >
              {t("references.mkb_create")}
            </Button>
          ) : undefined
        }
      />

      <Card padding="md">
        <Group justify="space-between" wrap="wrap">
          <TextInput
            rightSection={<IconSearch size={18} strokeWidth={2} />}
            placeholder={t("references.mkb10_search")}
            value={q}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 280 }}
          />
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              {t("audit.page_size")}:
            </Text>
            <Select
              size="sm"
              w={92}
              data={["25", "50", "100"]}
              value={String(pageSize)}
              onChange={onPageSizeChange}
            />
          </Group>
        </Group>
      </Card>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={680}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 120 }}>{t("references.mkb10_fields.code")}</Table.Th>
                <Table.Th>{t("references.mkb10_fields.description")}</Table.Th>
                {canEdit && <Table.Th style={{ width: 64 }}></Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={canEdit ? 3 : 2}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.length === 0 && !isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={canEdit ? 3 : 2}>
                    <EmptyState
                      icon={<IconBook size={32} strokeWidth={1.5} />}
                      title={t("references.mkb10_empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.map((m) => (
                <Table.Tr key={m.code}>
                  <Table.Td>
                    <Text ff="monospace" fw={700} c="brand.7" size="sm">
                      {m.code}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{getDescription(m)}</Text>
                  </Table.Td>
                  {canEdit && (
                    <Table.Td>
                      <Menu position="bottom-end" shadow="md">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" radius="xl">
                            <IconChevronDown size={16} strokeWidth={2} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<Pencil size={14} strokeWidth={2.2} />}
                            onClick={() => onEdit(m)}
                          >
                            {t("common.edit")}
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconTrash size={14} strokeWidth={2.2} />}
                            color="red"
                            onClick={() => onDelete(m)}
                          >
                            {t("common.delete")}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {totalPages > 1 && (
        <Group justify="center" mt="sm">
          <Pagination
            total={totalPages}
            value={page}
            onChange={setPage}
            radius="md"
            size="sm"
          />
        </Group>
      )}

      <Mkb10FormDialog
        opened={dialogOpen}
        onClose={() => {
          dialogHandlers.close();
          setEditing(null);
        }}
        initial={editing}
      />

      <Modal
        opened={!!deleting}
        onClose={() => setDeleting(null)}
        title={t("references.mkb_delete_confirm_title")}
        size="sm"
        radius="lg"
      >
        <Stack>
          <Text size="sm">
            {t("references.mkb_delete_confirm", { code: deleting?.code ?? "" })}
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={() => setDeleting(null)}>
              {t("common.cancel")}
            </Button>
            <Button color="red" onClick={confirmDelete} loading={deleteMutation.isPending}>
              {t("common.delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
